import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AccessRole, CustomTableComponent, DataColumnType,
  ICustomTable, LoadingPanelService, LocalService,
  CustomAlertComponent, CustomTitle, FormGroupErrorMessageComponent,
  ButtonColumnType,
} from 'lib-base';
import { TranslateModule, TranslateService, TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { TooltipModule } from 'primeng/tooltip';
import { TipoIdentificacionService } from '../../../services/database/tipoIdentificacion.service';
import { ITipoIdentificacion } from '../../../shared/interfaces/database/seguridad';
import { SisbenNucleoFamiliarService } from '../../../services/sisben/sisben-nucleo-familiar.service';
import { IGetPerfilUsuario } from '../../../shared/interfaces/sisben/sisben';
import { SisbenGestionFormularioComponent } from './sisben-gestion-formulario.component';

@Component({
  selector: 'app-sisben-gestion',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, TranslateModule,
    CustomTableComponent, CustomAlertComponent, ConfirmPopupModule, CustomTitle,
    DividerModule, DropdownModule, ButtonModule, InputNumberModule, FormGroupErrorMessageComponent,
    TranslatePipe, TooltipModule, SisbenGestionFormularioComponent
  ],
  providers: [ConfirmationService],
  styles:`
    .buscar__wrapper {
      padding: 1rem 1rem 1rem 1rem;
    }

    .custom-btn-padding{
      padding: 14px 16px;
    }
  `,
  template: `
    <section class="flex justify-content-center mt-4">
      <div class="card-table col-12">
        <CustomTitle [title]="'app-menu.gestion-sisben' | translate"
                    styleClass="font-principal app-title-page-color font-medium font-23 mt-4 text-uppercase mb-0"/>
        
        <form [formGroup]="searchForm" (ngSubmit)="onSubmit()" class="buscar__wrapper">
          <div class="grid p-fluid">           
            <div class="col-12 md:col-4 lg:col-5">
              <p-dropdown id="tipoIdentificacion" [options]="tiposIdentificaciones"
                          formControlName="tipoIdentificacion" optionValue="id"
                          optionLabel="descripcion" [showClear]="true"
                          placeholder="Seleccione un tipo de identificación"/>
              @if (searchForm.get('tipoIdentificacion')?.invalid && searchForm.get('tipoIdentificacion')?.touched) {
                <app-form-group-error-message
                  [control]="searchForm.get('tipoIdentificacion')"/>
              }
            </div>

            <div class="col-12 md:col-4 lg:col-5">
              <p-inputNumber inputId="identificacion" formControlName="identificacion"
                            [useGrouping]="false" mode="decimal"
                            placeholder="Ingrese el número de documento"/>
              @if (searchForm.get('identificacion')?.invalid && searchForm.get('identificacion')?.touched) {
                <app-form-group-error-message [control]="searchForm.get('identificacion')"/>
              }
            </div>

            <div class="col-12 md:col-4 lg:col-2 flex gap-2 justify-content-start align-items-end">
              <button pButton icon="pi pi-eraser" type="button" (click)="onClean()"
                      class=" p-button-outlined w-fit custom-btn-padding"></button>
              <button pButton icon="pi pi-search" type="submit" [disabled]="disabledSubmit"
                      [pTooltip]="'common.buscarFamilia' | translate"
                      tooltipPosition="top"
                      [tooltipDisabled]="disabledSubmit"
                      class=" p-button-outlined w-fit custom-btn-padding"></button>
            </div>
          </div>
        </form>
        <custom-table #customTableComponent [config]="customTable" (emitterLazyLoad)="getRecords($event)"></custom-table>
        <custom-alert />
      </div>
    </section>

    <app-sisben-gestion-formulario #formularioComponent (formularioGuardado)="onFormularioGuardado()"></app-sisben-gestion-formulario>
  `
})
export class SisbenGestionComponent implements OnInit {

  perfiles !: IGetPerfilUsuario[];
  translations: any = {};
  customTable !: ICustomTable;
  role = AccessRole;
  tiposIdentificaciones: ITipoIdentificacion[] = [];
  searchForm!: FormGroup;
  tipoIdentificacionFiltro?: number;
  identificacionFiltro?: string;

  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;
  @ViewChild(SisbenGestionFormularioComponent) formularioComponent !: SisbenGestionFormularioComponent;

  get disabledSubmit(): boolean {
    return this.searchForm.invalid;
  }

  constructor(
    private readonly _snds: SisbenNucleoFamiliarService,
    private readonly tis: TipoIdentificacionService,
    private readonly lps: LoadingPanelService,
    public readonly ls: LocalService,
    private readonly t: TranslateService
  ) {
    this.getTipoIdentificacion();
    this.initForm();
  }

  ngOnInit() {
    this.translate().then(() => this.configTable());
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      primerNombre: this.t.get('common.primerNombre'),
      segundoNombre: this.t.get('common.segundoNombre'),
      primerApellido: this.t.get('common.primerApellido'),
      segundoApellido: this.t.get('common.segundoApellido'),
      tipoIdentificacion: this.t.get('common.tipoIdentificacion'),
      identificacion: this.t.get('common.identificacion'),
      esJefeHogar: this.t.get('common.esJefeHogar'),
      fechaExpedicion: this.t.get('common.fechaExpedicion'),
      lugarExpedicion: this.t.get('common.lugarExpedicion'),
      fechaNacimiento: this.t.get('common.fechaNacimiento'),
      barrio: this.t.get('common.barrio'),
      direccion: this.t.get('common.direccion'),
      telefonoMovil: this.t.get('common.telefonoMovil'),
      telefonoFijo: this.t.get('common.telefonoFijo'),
      genero: this.t.get('common.genero'),
      vinculo: this.t.get('common.vinculo'),
      puntajeSisben: this.t.get('common.puntajeSisben'),
      nombreCompleto: this.t.get('common.nombreCompleto'),
      requiereRolSeguridad: this.t.get('msg.requiere-rol-seguridad'),
    }));
  }

  //#region table
  configTable() {
    this.customTable = {
      header: {
        //esto existe para las columnas opcionales
      },
      columns: [
        {
          field: 'primerNombre',
          header: this.translations.primerNombre,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'segundoNombre',
          header: this.translations.segundoNombre,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'primerApellido',
          header: this.translations.primerApellido,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'segundoApellido',
          header: this.translations.segundoApellido,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'tipoIdentificacion',
          header: this.translations.tipoIdentificacion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'identificacion',
          header: this.translations.identificacion,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold'
          },
          columnFilter: true,
          width: '10rem',
          dataType: DataColumnType.TEXT
        },
        {
          field: 'jefeHogar',
          header: this.translations.esJefeHogar,
          sort: true,
          width: '5rem',
          dataType: DataColumnType.CHECK
        },
        {
          field: 'fechaExpedicion',
          header: this.translations.fechaExpedicion,
          sort: true,
          width: '10rem',
          dataType: DataColumnType.DATE,
          optional: true
        },
        {
          field: 'lugarExpedicion',
          header: this.translations.lugarExpedicion,
          sort: true,
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'fechaNacimiento',
          header: this.translations.fechaNacimiento,
          sort: true,
          dataType: DataColumnType.DATE,
          optional: true
        },
        {
          field: 'barrio',
          header: this.translations.barrio,
          sort: true,
          width: '10rem',
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'direccion',
          header: this.translations.direccion,
          sort: true,
          width: '10rem',
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'telefonoMovil',
          header: this.translations.telefonoMovil,
          sort: true,
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'telefonoFijo',
          header: this.translations.telefonoFijo,
          sort: true,
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'genero',
          header: this.translations.genero,
          sort: true,
          width: '8rem',
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'vinculo',
          header: this.translations.vinculo,
          sort: true,
          width: '8rem',
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'puntajeSisben',
          header: this.translations.puntajeSisben,
          sort: true,
          width: '10rem',
          dataType: DataColumnType.TEXT,
          optional: true
        },
        {
          field: 'edit',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-1 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.SISBEN),
          tooltip: {
            message: this.translations.requiereRolSeguridad
          },
          onClick: (event: Event, data: IGetPerfilUsuario) => this.formularioComponent.show(data)
        }
      ],
      settings: {
        columnToggle: {
          showColumnToggle: true,
          maxSelectedLabels: 5
        },
        table: {
          lazy: true,
          rows: 8,
        }
      }
    } as ICustomTable;
  }

  // #endregion table

  //#region search
  private initForm() {
    this.searchForm = new FormGroup({
      identificacion: new FormControl<number | null>(null, Validators.required),
      tipoIdentificacion: new FormControl<string | null>(null, Validators.required)
    })
  }

  onSubmit() {
    this.search();
  }

  onClean(): void {
    this.searchForm.reset();
    this.tipoIdentificacionFiltro = undefined;
    this.identificacionFiltro = undefined;
    if (this.customTableComponent) {
      this.getRecords();
    }
  }

  private search() {
    const searchValue = this.searchForm.value;
    this.tipoIdentificacionFiltro = searchValue.tipoIdentificacion;
    this.identificacionFiltro = searchValue.identificacion;
    // Resetear la tabla al primer registro con nuevos criterios
    if (this.customTableComponent) {
      this.getRecords();
    }
  }

  getRecords($event?: any): void {
    this.lps.show(true);
    this._snds.getAllPerfiles(
      $event ?? {},
      this.tipoIdentificacionFiltro,
      this.identificacionFiltro
    ).subscribe({
      next: (data: any) => {
        if (this.customTableComponent) {
          this.customTableComponent.data = data.content ? data.content : [];
          this.customTableComponent.totalRecords = data.totalElements;
        }
        this.lps.hide();
      },
      error: () => {
        this.lps.hide();
        if (this.customTableComponent) {
          this.customTableComponent.data = [];
        }
      }
    });
  }

  private getTipoIdentificacion() {
    this.tis.getTipoIdentificaciones().subscribe({
      next: data => {
        this.tiposIdentificaciones = data;
      }
    })
  }

  onFormularioGuardado(): void {
    this.getRecords();
  }
  // #endregion search
}